/* eslint-disable jsx-a11y/anchor-is-valid */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date: Tue Apr 02 2024 13:51:44
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import {
  Button,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  Stack,
  TextContainer,
  Card,
  TextStyle
} from '@shopify/polaris';

import { getFormValues, reduxForm, reset, Field } from 'redux-form';

import TitleHelp from 'src/components/views/common/title-help';
import DidYouKnow from 'src/components/views/search-personalization/did-you-know';
import MetricsAllowcation from 'src/components/views/search-personalization/metrics-allowcation';
import ProcessingBanner from 'src/components/views/advanced-search/processing-banner';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderSelect } from 'src/components/views/common/forms/render-select';

import history from 'src/routes/history';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';
import { MAXIMUM_RE_RANK_ITEMS } from 'src/constants/search';
import { URL } from 'src/constants/links';
import * as searchPersonalizationAction from 'actions/search-personalization-action';
import { trackFSEvent_SearchPersonalization } from 'src/integrations/fullStory';

import type { ComposedProps } from './index.type';

const SearchPersonalization = (props: ComposedProps) => {
  const {
    i18n,
    settings,
    formValues = {},
    pristine,
    updating,
    resetForm,
    dirty,
    updateSettings,
    getSettings,
    loading,
    isNewSearch,
    enableAISearch
  } = props;

  const { enabled } = formValues;
  const available = _.get(settings, 'available', false);
  const enabledSettings = _.get(settings, 'enabled', false);

  useEffect(() => {
    if (isNewSearch && enableAISearch) {
      getSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewSearch]);

  const handleSave = data => {
    const updatedData = {
      ...data,
      numberReordered: parseInt(data.numberReordered)
    };
    updateSettings(updatedData);
    // Tracking even enable/disabled semantic search
    // mixpanelV3Helper.trackProductPerformanceRanking(settings, data);

    // Track Full Story event
    trackFSEvent_SearchPersonalization(settings, data);
  };

  const checkOpenConfirmDisabled = () => {
    handleSave(formValues);
  };

  if (!enableAISearch) return null;
  const reorderOptions = MAXIMUM_RE_RANK_ITEMS.map(e => ({ value: `${e}`, label: `${e}` }));
  return (
    <div className={`BC-Page BC-Page__SearchPersonalization`}>
      <Page
        title={i18n.translate('AdminHTML.SearchPersonalization.SearchPersonalization.title')}
        primaryAction={
          <div className="BC-Page BC-Page__Button">
            <Stack>
              {!pristine && (
                <Button disabled={updating} onClick={resetForm}>
                  {i18n.translate('AdminHTML.Common.Action.discard')}
                </Button>
              )}
              <Button
                primary
                onClick={checkOpenConfirmDisabled}
                disabled={pristine}
                loading={updating}>
                {i18n.translate('AdminHTML.Common.Action.saveSettings')}
              </Button>
            </Stack>
          </div>
        }>
        <TitleHelp
          i18n={i18n}
          // onPlay={() => setYoutubeModal(true)}
          hrefUserManual={URL.Link_Search_Personalization_Guide}
        />
        <Layout>
          <Layout.Section>
            <div className="BC-Container__Notifications">
              {enableAISearch && !_.isEmpty(settings) && !available && enabledSettings ? (
                <ProcessingBanner />
              ) : (
                <DidYouKnow />
              )}
            </div>
          </Layout.Section>
          <Layout.Section>
            <Card sectioned>
              <div>
                {loading ? (
                  <TextContainer>
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText />
                  </TextContainer>
                ) : (
                  <Field
                    name="enabled"
                    component={RenderToggleSwitch}
                    disabled={!isNewSearch || !enableAISearch}
                    label={i18n.translate(
                      'AdminHTML.SearchPersonalization.SearchPersonalization.enableSettings'
                    )}
                  />
                )}
              </div>
            </Card>
            {enabled && (
              <Card sectioned>
                <div className="settings-infos">
                  {/* Metrics allowcation */}
                  <MetricsAllowcation />
                  {/* Maximum re-order rank items */}
                  <div>
                    <Field
                      name="numberReordered"
                      component={RenderSelect}
                      label={i18n.translate(
                        'AdminHTML.SearchPersonalization.SearchPersonalization.input_Label'
                      )}
                      options={reorderOptions}
                      tooltip={i18n.translate(
                        'AdminHTML.SearchPersonalization.SearchPersonalization.tooltipInput_Content'
                      )}
                    />
                    <div style={{ color: '#6D7175' }}>
                      {i18n.translate(
                        'AdminHTML.SearchPersonalization.SearchPersonalization.warning'
                      )}
                    </div>
                  </div>
                  <div className="note">
                    {i18n.translate('AdminHTML.SearchPersonalization.SearchPersonalization.note', {
                      t: (
                        <TextStyle variation="strong">
                          {i18n.translate(
                            'AdminHTML.SearchPersonalization.SearchPersonalization.note_t'
                          )}
                        </TextStyle>
                      )
                    })}
                  </div>
                </div>
              </Card>
            )}
          </Layout.Section>
        </Layout>
      </Page>
      <RouteLeavingGuard
        when={dirty}
        // Navigate function
        navigate={path => history.push(path)}
        shouldBlockNavigation={dirty}
      />
    </div>
  );
};

const SearchPersonalizationForm = reduxForm({
  form: 'SearchPersonalizationForm',
  enableReinitialize: true
})(SearchPersonalization);

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: () => dispatch(reset('SearchPersonalizationForm')),
  updateSettings: data => dispatch(searchPersonalizationAction.updateSettings(data, ownProps.i18n)),
  getSettings: () => dispatch(searchPersonalizationAction.getSettings())
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('SearchPersonalizationForm')(state);
  const settings = _.get(state.searchPersonalization, 'settings', {});
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const isNewSearch = allowUsingNewSearchSettings(searchVersion);

  return {
    formValues,
    initialValues: _.isEmpty(settings)
      ? { enabled: false, numberReordered: 50 }
      : {
          ...settings,
          numberReordered: `${settings.numberReordered ? settings.numberReordered : 50}`
        },
    settings,
    updating: _.get(state, 'searchPersonalization.updating', false),
    loading: _.get(state, 'searchPersonalization.loading', false),
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    ),
    isNewSearch
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SearchPersonalizationForm));
